import { useAuth } from '@/hooks/useAuth';
import { useFeature } from '@/hooks/useCapabilities';

/**
 * Whether MSP surfaces should be shown, combining the product's two gating axes:
 *
 * - **Edition** (`useFeature('msp')`) — does this build include MSP at all? In
 *   the trial the code is compiled out of the backend and aliased away in the
 *   bundle, so this is false.
 * - **Organization** (`organizations.is_msp`) — has *this* org opted into
 *   operating as a Managed Service Provider? A self-service toggle in
 *   organization settings.
 *
 * Both must hold. This is the per-org "Axis B" layer the backend
 * `capabilities.rs` docstring describes as a follow-up: `/api/capabilities` is
 * public and context-free, so it can only answer the edition question, and the
 * org answer rides along on the current-user payload instead.
 *
 * Pessimistic while loading or on error — it never flashes true, so a gated nav
 * entry can't appear and then vanish.
 *
 * Deliberately lives outside `@/features/msp`: that barrel is alias-swapped to a
 * teaser in trial builds, so a gating hook placed there would disappear exactly
 * where it is needed.
 */
export function useIsMsp(): boolean {
  const { user, isLoading, isError } = useAuth();
  const mspFeature = useFeature('msp');

  if (isLoading || isError || !user) return false;

  // The org of the *active context*, not the user's home org — an MSP tech who
  // switches into a customer's context is managing that org.
  const activeOrgId = user.activeContext?.organizationId ?? user.organizationId ?? null;
  const activeOrg =
    user.organizations.find((o) => o.id === activeOrgId) ?? user.organization ?? null;

  return mspFeature && (activeOrg?.isMsp ?? false);
}
